import React, {useRef, useState} from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  Input,
  InputAdornment,
  InputLabel, MenuItem, Select,
  Slide, Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import {TransitionProps} from "@material-ui/core/transitions/transition";
import {makeStyles} from "@material-ui/core/styles";
import {authRepository} from "../../Domain/Repositories/AuthRepository";
import {Formik, FormikProps} from "formik";
import LoginRequest from "../../Domain/Requests/Auth/LoginRequest";
import * as yup from "yup";
import Loading from "./Loading";
import ApiError from "../../Domain/ApiError";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const useStyles = makeStyles((theme) => ({
  form: {
    '& > *': {
      margin: theme.spacing(1),
    },
  },
  dialog: {
    alignItems: 'flex-start',
  },
  deleteFlex: {
    flex: 1,
  }
}));

export interface UserModalProps
{
  open: boolean;
  onClose: () => void;
}

export default function UserModal(props: UserModalProps)
{
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const {open, onClose} = props;
  const classes = useStyles();

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>();

  const formRef = useRef<FormikProps<LoginRequest>>(null);

  const schema = yup.object().shape({
    emailAddress: yup.string().required('Required'),
    password: yup.string().required('Required'),
  });

  const handleLogin = (value: LoginRequest) =>
  {
    setSubmitting(true);
    setSubmitError(undefined);

    authRepository.login(value)
      .then(() => setSubmitting(false))
      .catch((error: ApiError) =>
      {
        setSubmitError(error.message)
        setSubmitting(false)
      })
  }

  const handleLogout = () =>
  {
    authRepository.logout();
    onClose();
  }

  return (
    <Dialog
      classes={{scrollPaper: classes.dialog}}
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      TransitionComponent={Transition}
    >
      <Loading isLoading={submitting}>
        {authRepository.user !== undefined ? (
          <>
            <DialogTitle>User Details</DialogTitle>

            <DialogContent>
              Email Address: {authRepository.user.emailAddress}
            </DialogContent>

            <DialogActions>
              <Button onClick={onClose} color="primary">
                Close
              </Button>
              <Button onClick={handleLogout} color="primary">
                Logout
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle>Login</DialogTitle>

            <DialogContent>
              <Formik<LoginRequest>
                initialValues={{emailAddress: "", password: ""}}
                onSubmit={(value) => handleLogin(value)}
                validationSchema={schema}
                innerRef={formRef}
              >
                {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                  }) => (
                  <>
                    <FormControl fullWidth margin="normal"
                                 error={touched.emailAddress && errors.emailAddress !== undefined}>
                      <InputLabel htmlFor="component-error">Email Address</InputLabel>
                      <Input
                        name="emailAddress"
                        value={values.emailAddress}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />

                      <FormHelperText>{touched.emailAddress && errors.emailAddress}</FormHelperText>
                    </FormControl>

                    <FormControl fullWidth margin="normal" error={touched.password && errors.password !== undefined}>
                      <InputLabel htmlFor="component-error">Password</InputLabel>
                      <Input
                        name="password"
                        value={values.password}
                        type="password"
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />

                      <FormHelperText>{touched.password && errors.password}</FormHelperText>
                    </FormControl>
                  </>
                )}
              </Formik>

              {submitError != undefined && (
                <Typography component="h3" variant="subtitle1" color="error" gutterBottom>
                  {submitError}
                </Typography>
              )}
            </DialogContent>

            <DialogActions>
              <Button onClick={onClose} color="primary">
                Close
              </Button>
              <Button onClick={() => formRef.current?.handleSubmit()} color="primary">
                Login
              </Button>
            </DialogActions>
          </>
        )}
      </Loading>
    </Dialog>
  );
}